import { Form, message } from "antd";
import {
  GET_DAILY_FINISHING_LINE_PLAN_OPERATORS,
  GET_DAILY_FINISHING_LINE_PLAN_STYLES,
  GET_DAILY_FINISHING_LINE_PLAN_SUPERVISORS,
  GET_DIGITALIZATION_STYLES,
  GET_FINISHING_LINES,
  GET_ID_CARD_NO_USER,
  GET_PURCHASE_ORDER_MAP_LISTS,
  GET_SIZES,
} from "apiServices/API_ENDPOINTS";
import { getData, postData, putData } from "apiServices/common"; // <-- add postData import
import { createContext, useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router";

export const DailyFinishingLinePlanContext = createContext();

const DailyFinishingLinePlanContextProvider = ({ children }) => {
  const [form] = Form.useForm();
  const [dailyFinishingLinePlanData, setDailyFinishingLinePlanData] =
    useState(null);

  const [options, setOptions] = useState({
    finishingLines: [],
    styles: [],
    subStyles: [],
    colors: [],
    sizes: [],
    po: [],
    operators: [],
    supervisors: [],
  });

  // route
  const { id } = useParams();
  const navigate = useNavigate();

  // console.log({DailyFinishingLinePlanData})

  // get name
  const getNameById = useCallback(async (id) => {
    try {
      if (!id) return null;
      const response = await getData(`${GET_ID_CARD_NO_USER}/${id}`);
      return response?.data?.data || null;
    } catch (error) {
      console.error("getNameById -> error", error);
      return null;
    }
  }, []);

  // get finishing line options from API
  const getFinishingLines = useCallback(async (filterValues = {}) => {
    try {
      const params = {
        per_page: 5000,
      };

      // Add search filter if provided
      if (filterValues.search) {
        params.search = filterValues.search;
      }

      const response = await getData(GET_FINISHING_LINES, false, params);
      return response.data?.data?.data?.map((line) => ({
        ...line,
        value: line.id,
        label: line.name,
      }));
    } catch (error) {
      console.error("getFinishingLines -> error", error);
      return [];
    }
  }, []);

  // get PO Map options from API
  const getPOMap = useCallback(async (filterValues = {}) => {
    try {
      const params = {
        per_page: 5000,
      };
      if (filterValues.original_po) {
        params.original_po = filterValues.original_po;
      }
      const response = await getData(
        GET_PURCHASE_ORDER_MAP_LISTS,
        false,
        params,
      );
      return response.data?.data?.data?.map((po) => ({
        ...po,
        value: po.id,
        label: po.original_po,
      }));
    } catch (error) {
      console.error("getPOMap -> error", error);
      return [];
    }
  }, []);

  // PO search handler
  const onSearchPO = useCallback(
    async (searchValue) => {
      try {
        const filteredPOs = await getPOMap({ original_po: searchValue });
        setOptions((prev) => ({
          ...prev,
          po: filteredPOs,
        }));
      } catch (error) {
        console.error("onSearchPO -> error", error);
      }
    },
    [getPOMap],
  );

  // get sizes options from API
  const getSizes = useCallback(async (filterValues = {}) => {
    try {
      const params = {
        per_page: 5000,
      };

      // Use name key for search
      if (filterValues.search) {
        params.name = filterValues.search;
      }

      const response = await getData(GET_SIZES, false, params);
      return response.data?.data?.data?.map((size) => ({
        value: size.id,
        label: size.name,
      }));
    } catch (error) {
      console.error("getSizes -> error", error);
      return [];
    }
  }, []);

  // get supervisors options from API
  const getSupervisors = useCallback(async (filterValues = {}) => {
    try {
      const params = {
        per_page: 50,
      };

      // Add type and finishing_line_id if provided
      if (filterValues.type) {
        params.type = filterValues.type;
      }
      if (filterValues.finishing_line_id) {
        params.finishing_line_id = filterValues.finishing_line_id;
      }

      // Use id_card_no key for search
      if (filterValues.id_card_no) {
        params.id_card_no = filterValues.id_card_no;
      }

      const response = await getData(
        GET_DAILY_FINISHING_LINE_PLAN_SUPERVISORS,
        false,
        params,
      );
      return response.data?.data?.map((supervisor) => ({
        ...supervisor,
        label: `${supervisor.additional_info?.id_card_no} (${supervisor.name})`,
        value: supervisor.additional_info?.user_id,
      }));
    } catch (error) {
      console.error("getSupervisors -> error", error);
      return [];
    }
  }, []);

  // get Style options from API OLD
  // const getStyles = useCallback(async (filterValues = {}) => {
  //   try {
  //     const bodyData = {};

  //     // Use style_no for search
  //     if (filterValues?.style_no) {
  //       bodyData.style_no = filterValues.style_no;
  //     } else if (filterValues?.name?.name) {
  //       bodyData.style_no = filterValues.name.name;
  //     }

  //     const response = await getData(
  //       GET_DIGITALIZATION_STYLES,
  //       false,
  //       bodyData,
  //     );
  //     return response.data?.data?.data?.map((style) => ({
  //       ...style,
  //       value: style.id,
  //       label: style.style_no,
  //     }));
  //   } catch (error) {
  //     console.error("getStyles -> error", error);
  //     return [];
  //   }
  // }, []);

  const getStyles = useCallback(async (filterValues = {}) => {
    try {
      const bodyData = {};

      // Use style_no for search
      if (filterValues?.style_no) {
        bodyData.style_no = filterValues.style_no;
      } else if (filterValues?.name?.name) {
        bodyData.style_no = filterValues.name.name;
      }

      const response = await getData(
        GET_DAILY_FINISHING_LINE_PLAN_STYLES,
        false,
        bodyData,
      );
      return response.data?.data?.data?.map((style) => ({
        ...style,
        value: style.id,
        label: style.style_no,
      }));
    } catch (error) {
      console.error("getStyles -> error", error);
      return [];
    }
  }, []);

  // get operators options from API
  const getOperators = useCallback(async (filterValues = {}) => {
    try {
      const params = {
        per_page: 50,
      };
      if (filterValues.id_card_no) {
        params.id_card_no = filterValues.id_card_no;
      }
      if (filterValues.finishing_line_id) {
        params.finishing_line_id = filterValues.finishing_line_id;
      }
      if (filterValues.supervisor_id) {
        params.supervisor_id = filterValues.supervisor_id;
      }
      if (filterValues.type) {
        params.type = filterValues.type;
      }
      const response = await getData(
        GET_DAILY_FINISHING_LINE_PLAN_OPERATORS,
        false,
        params,
      );
      return response.data?.data?.map((operator) => ({
        ...operator,
        value: operator.operator.id,
        label: `${
          operator?.operator?.additional_info?.id_card_no ||
          operator?.additional_info?.id_card_no ||
          operator?.id_card_no ||
          ""
        }`,
      }));
    } catch (error) {
      console.error("getOperators -> error", error);
      return [];
    }
  }, []);
  // Operator search handler
  const onSearchOperator = useCallback(
    async (searchValue) => {
      const finishingLineId = form.getFieldValue("finishing_line_id");
      const supervisorId = form.getFieldValue("supervisor_id");
      const type = form.getFieldValue("type");
      if (!type || !finishingLineId || !supervisorId) {
        setOptions((prev) => ({
          ...prev,
          operators: [],
        }));
        return;
      }
      try {
        const filteredOperators = await getOperators({
          id_card_no: searchValue,
          finishing_line_id: finishingLineId,
          supervisor_id: supervisorId,
          type: type,
        });
        setOptions((prev) => ({
          ...prev,
          operators: filteredOperators,
        }));
      } catch (error) {
        console.error("onSearchOperator -> error", error);
      }
    },
    [getOperators, form],
  );

  const onFinish = useCallback(
    async (values) => {
      let getOperatorsValues = [];
      if (id) {
        // For updates, just use the form values directly (no delete logic)
        getOperatorsValues = values.operations?.map((op) => ({
          id: op.id || 0,
          operator_id: op.operator_id,
          hourly_target: op.hourly_target,
          is_delete: 0,
        }));
      } else {
        // For new entries, just use the form values directly
        getOperatorsValues = values.operations?.map((op) => ({
          id: op.id || 0,
          operator_id: op.operator_id,
          hourly_target: op.hourly_target,
          is_delete: 0,
        }));
      }

      // Transform the data to match the API structure
      const formattedData = {
        plan_date: values.plan_date,
        type: values.type,
        finishing_line_id: values.finishing_line_id,
        po_map_id: values.po_map_id,
        style_id: values.style_id,
        sub_style_id: values.sub_style_id || null,
        color_id: values.color_id || null,
        size_id: values.size_id || null,
        supervisor_id: values.supervisor_id || null,
        sales_contract_id: values.sales_contract_id || null,
        buying_office_id: values.buying_office_id || null,
        buyer_id: values.buyer_id || null,
        sub_contract_factory_id: values.sub_contract_factory_id || null,
        operator_details: getOperatorsValues,
      };

      const query = id
        ? `/api/digitalization/daily-finishing-line-plan-update/${id}`
        : "/api/digitalization/daily-finishing-line-plan-store";

      // Submit to API
      try {
        const response = id
          ? await putData(query, formattedData)
          : await postData(query, formattedData);
        if (response?.code === 200) {
          message.success(
            response?.message ||
              "Daily Finishing Line Plan updated successfully",
          );
          navigate("/digitalization/daily-finishing-line-plan");
        }
      } catch (error) {
        console.log("API error:", error);
        message.error(
          error?.message || "Failed to save Daily Finishing Line Plan",
        );
      }
    },
    [id, navigate],
  );

  const onChangeTypeOrLine = useCallback(
    async (type, finishingLineId) => {
      if (type && finishingLineId) {
        try {
          const supervisors = await getSupervisors({
            type,
            finishing_line_id: finishingLineId,
          });

          setOptions((prev) => ({
            ...prev,
            supervisors,
          }));
        } catch (error) {
          console.error("onChangeTypeOrLine -> error", error);
        }
      }
    },
    [getSupervisors],
  );

  // OLD - onChangeStyle to fetch and set related fields
  // const onChangeStyle = useCallback(
  //   async (value) => {
  //     if (value) {
  //       try {
  //         // reset fields
  //         form.setFieldsValue({
  //           sub_style_id: undefined,
  //           color_id: undefined,
  //           buyer: undefined,
  //           buyer_id: undefined,
  //           buying_office: undefined,
  //           buying_office_id: undefined,
  //           factory: undefined,
  //           sub_contract_factory_id: undefined,
  //           sales_contract: undefined,
  //           sales_contract_id: undefined,
  //         });
  //         // Fetch style details to populate related fields
  //         const response = await getData(`/api/styles/${value}/edit`);
  //         const styleData = response.data?.data?.[0]?.style; // Fix: Access nested style data

  //         console.log("Style API Response:", response.data);
  //         console.log("Extracted Style Data:", styleData);

  //         if (styleData) {
  //           // Update colors and sub-styles based on selected style
  //           const colors = styleData.color_map || [];
  //           const subStyles = styleData.sub_style_map || [];

  //           console.log("Colors:", colors);
  //           console.log("Sub-styles:", subStyles);

  //           setOptions((prev) => ({
  //             ...prev,
  //             colors,
  //             subStyles,
  //           }));

  //           // Auto-populate related fields
  //           form.setFieldsValue({
  //             sales_contract:
  //               styleData.active_sales_contract_map_info
  //                 ?.active_sales_contract_info?.reference_no,
  //             sales_contract_id:
  //               styleData.active_sales_contract_map_info
  //                 ?.active_sales_contract_info?.id,
  //             buyer: styleData.buyer_info?.name,
  //             buyer_id: styleData.buyer_id,
  //             buying_office: styleData.buying_office_info?.name,
  //             buying_office_id: styleData.buying_office_id,
  //             factory: styleData.factory_info?.name,
  //             sub_contract_factory_id: styleData.sub_contract_factory_id,
  //           });
  //         }
  //       } catch (error) {
  //         console.error("onChangeStyle -> error", error);
  //       }
  //     } else {
  //       // Clear related fields when style is cleared
  //       setOptions((prev) => ({
  //         ...prev,
  //         colors: [],
  //         subStyles: [],
  //       }));

  //       form.setFieldsValue({
  //         sub_style_id: undefined,
  //         color_id: undefined,
  //         sales_contract: undefined,
  //         sales_contract_id: undefined,
  //         buyer: undefined,
  //         buyer_id: undefined,
  //         buying_office: undefined,
  //         buying_office_id: undefined,
  //         factory: undefined,
  //         sub_contract_factory_id: undefined,
  //       });
  //     }
  //   },
  //   [form],
  // );

  const onChangeStyle = useCallback(
  async (value) => {
    if (value) {
      try {
        // reset fields
        form.setFieldsValue({
          sub_style_id: undefined,
          color_id: undefined,
          size_id: undefined,
          po_map_id: undefined,
          buyer: undefined,
          buyer_id: undefined,
          buying_office: undefined,
          buying_office_id: undefined,
          factory: undefined,
          sub_contract_factory_id: undefined,
          sales_contract: undefined,
          sales_contract_id: undefined,
        });

        // Find the selected style from options
        const selectedStyle = options.styles.find(style => style.id === value);
        
        if (selectedStyle) {
          // Extract all relevant data from the selected style
          const colors = selectedStyle.color_map?.map(color => ({
            ...color,
            value: color.color_id,
            label: color.color_info?.name
          })) || [];
          
          const sizes = selectedStyle.sizes?.map(size => ({
            ...size,
            value: size.size_id,
            label: size.sizes?.name
          })) || [];

          const subStyles = selectedStyle.sub_style?.map(subStyle => ({
            ...subStyle,
            value: subStyle.id,
            label: subStyle.sub_style_no
          })) || [];
          
          const poMaps = selectedStyle.po_maps?.map(po => ({
            ...po,
            value: po.id,
            label: po.original_po
          })) || [];

          // Update options with style-related data
          setOptions(prev => ({
            ...prev,
            colors,
            subStyles,
            sizes: sizes.length > 0 ? sizes : prev.sizes,
            po: poMaps.length > 0 ? poMaps : prev.po,
          }));

          // Auto-populate related fields
          form.setFieldsValue({
            sales_contract: selectedStyle.active_sales_contract?.reference_no,
            sales_contract_id: selectedStyle.active_sales_contract?.id,
            buyer: selectedStyle.buyer_info?.name,
            buyer_id: selectedStyle.buyer_id,
            buying_office: selectedStyle.buying_office_info?.name,
            buying_office_id: selectedStyle.buying_office_id,
            factory: selectedStyle.factory_info?.name,
            sub_contract_factory_id: selectedStyle.sub_contract_factory_id,
          });
        }
      } catch (error) {
        console.error("onChangeStyle -> error", error);
      }
    } else {
      // Clear related fields when style is cleared
      setOptions((prev) => ({
        ...prev,
        colors: [],
        subStyles: [],
        sizes: prev.sizes, // Keep default sizes
        po: prev.po, // Keep default POs
      }));

      form.setFieldsValue({
        sub_style_id: undefined,
        color_id: undefined,
        size_id: undefined,
        po_map_id: undefined,
        sales_contract: undefined,
        sales_contract_id: undefined,
        buyer: undefined,
        buyer_id: undefined,
        buying_office: undefined,
        buying_office_id: undefined,
        factory: undefined,
        sub_contract_factory_id: undefined,
      });
    }
  },
  [form, options.styles]
);


  const onSearchStyle = useCallback(
    async (searchValue) => {
      try {
        const filteredStyles = await getStyles({ style_no: searchValue });
        setOptions((prev) => ({
          ...prev,
          styles: filteredStyles,
        }));
      } catch (error) {
        console.error("onSearchStyle -> error", error);
      }
    },
    [getStyles],
  );

  const onSearchSize = useCallback(
    async (searchValue) => {
      try {
        const filteredSizes = await getSizes({ name: searchValue });
        setOptions((prev) => ({
          ...prev,
          sizes: filteredSizes,
        }));
      } catch (error) {
        console.error("onSearchSize -> error", error);
      }
    },
    [getSizes],
  );

  const onSearchSupervisor = useCallback(
    async (searchValue) => {
      console.log("onSearchSupervisor -> searchValue", searchValue);
      try {
        const type = form.getFieldValue("type");
        const finishingLineId = form.getFieldValue("finishing_line_id");

        if (type && finishingLineId) {
          const filteredSupervisors = await getSupervisors({
            type,
            finishing_line_id: finishingLineId,
            id_card_no: searchValue,
          });
          setOptions((prev) => ({
            ...prev,
            supervisors: filteredSupervisors,
          }));
        }
      } catch (error) {
        console.error("onSearchSupervisor -> error", error);
      }
    },
    [getSupervisors, form],
  );

  const onChangeSupervisorId = useCallback(
    async (value) => {
      if (value) {
        try {
          // Fetch operator details based on selected supervisor
          const operators = await getOperators({
            supervisor_id: value,
            finishing_line_id: form.getFieldValue("finishing_line_id"),
            type: form.getFieldValue("type"),
          });
          setOptions((prev) => ({
            ...prev,
            operators,
          }));
          const mapOperators = operators.map((op) => ({
            operator_id: op.value,
            operator_name: op?.operator?.name,
            hourly_target: 0,
          }));
          form.setFieldsValue({
            operations: mapOperators,
          });
        } catch (error) {
          console.error("onChangeSupervisorId -> error", error);
        }
      } else {
        // Clear operators when supervisor is cleared
        setOptions((prev) => ({
          ...prev,
          operators: [],
        }));
        form.setFieldsValue({
          operations: [],
        });
      }
    },
    [form, getOperators],
  );

  const onChangeOperatorId = useCallback(
    async (value) => {
      if (value) {
        const operatorData = await getNameById(value);
        if (operatorData) {
          form.setFieldsValue({
            operator: {
              name: operatorData.name,
            },
          });
        }
      }
    },
    [form, getNameById],
  );

  useEffect(() => {
    const fetchData = async () => {
      const finishingLines = await getFinishingLines();
      const styles = await getStyles();
      const po = await getPOMap();
      const sizes = await getSizes();

      setOptions((prev) => ({
        ...prev,
        finishingLines,
        styles,
        po,
        sizes,
      }));
    };

    fetchData();
  }, [getFinishingLines, getStyles, getPOMap, getSizes]);

  useEffect(() => {
    if (id) {
      const fetchLinePlanData = async () => {
        try {
          const response = await getData(
            `/api/digitalization/daily-finishing-line-plan-show/${id}`,
          );
          const data = response?.data?.data;
          setDailyFinishingLinePlanData(data);

          // Fetch related options based on the fetched data
          const styles = await getStyles({
            style_no: data.style_info?.style_no,
          });
          onChangeStyle(data.style_id);
          const supervisors = await getSupervisors({
            type: data.type,
            finishing_line_id: data.finishing_line_id,
            id_card_no: data.supervisor_info?.additional_info?.id_card_no,
          });
          const operators = await getOperators({
            finishing_line_id: data.finishing_line_id,
            supervisor_id: data.supervisor_id,
            type: data.type,
          });

          const fetchedSubStyleOption = data?.sub_style_info
            ? {
                id: data.sub_style_info.id,
                sub_style_no: data.sub_style_info.sub_style_no,
                value: data.sub_style_info.id,
                label: data.sub_style_info.sub_style_no,
              }
            : null;

          const fetchedColorOption = data?.color_info
            ? {
                color_id: data.color_info.id,
                color_info: { name: data.color_info.name },
                value: data.color_info.id,
                label: data.color_info.name,
              }
            : null;

          const fetchedSizeOption = data?.size_info
            ? {
                value: data.size_info.id,
                label: data.size_info.name,
              }
            : null;

          setOptions((prev) => {
            const nextSubStyles = [...(prev.subStyles || [])];
            if (
              fetchedSubStyleOption &&
              !nextSubStyles.some((s) => s?.id === fetchedSubStyleOption.id)
            ) {
              nextSubStyles.unshift(fetchedSubStyleOption);
            }

            const nextColors = [...(prev.colors || [])];
            if (
              fetchedColorOption &&
              !nextColors.some((c) => c?.color_id === fetchedColorOption.color_id)
            ) {
              nextColors.unshift(fetchedColorOption);
            }

            const nextSizes = [...(prev.sizes || [])];
            if (
              fetchedSizeOption &&
              !nextSizes.some((s) => s?.value === fetchedSizeOption.value)
            ) {
              nextSizes.unshift(fetchedSizeOption);
            }

            return {
              ...prev,
              styles,
              supervisors,
              operators,
              subStyles: nextSubStyles,
              colors: nextColors,
              sizes: nextSizes,
            };
          });

          form.setFieldsValue({
            plan_date: data.plan_date,
            type: data.type,
            finishing_line_id: data.finishing_line_id,
            po_map_id: data.po_map_id,
            style_id: data.style_id,
            sub_style_id: data.sub_style_id,
            color_id: data.color_id,
            size_id: data.size_id,
            supervisor_id: data.supervisor_id,
            sales_contract_id: data.sales_contract_id,
            buying_office_id: data.buying_office_id,
            buyer_id: data.buyer_id,
            sub_contract_factory_id: data.sub_contract_factory_id,
            operations: operators.map((op, i) => {
              const find = data.details?.find(
                (o) => o.operator_id === op.operator_id,
              );

              if (find) {
                return {
                  id: find.id || 0,
                  operator_id: find.operator_id,
                  operator_name: find?.operator_info?.name,
                  hourly_target: find.hourly_target || 0,
                  is_delete: find.is_delete || 0,
                };
              }

              return {
                id: 0,
                operator_id: op.operator_id,
                operator_name: op?.operator?.name,
                hourly_target: op.hourly_target || 0,
                is_delete: op.is_delete || 0,
              };
            }),
          });
        } catch (error) {
          console.error("fetchLinePlanData -> error", error);
          message.error("Failed to fetch Daily Finishing Line Plan data");
        }
      };
      fetchLinePlanData();
    } else {
      // Reset form if no ID is provided
      form.resetFields();
      const fetchData = async () => {
        const finishingLines = await getFinishingLines();
        const styles = await getStyles();
        const po = await getPOMap();
        const sizes = await getSizes();

        setOptions((prev) => ({
          ...prev,
          finishingLines,
          styles,
          po,
          sizes,
        }));
      };

      fetchData();
    }
  }, [
    id,
    form,
    getSupervisors,
    getOperators,
    getFinishingLines,
    getStyles,
    getPOMap,
    getSizes,
  ]);

  return (
    <DailyFinishingLinePlanContext.Provider
      value={{
        form,
        options,
        setOptions,
        getNameById,
        onFinish,
        onChangeTypeOrLine,
        onChangeStyle,
        onChangeSupervisorId,
        onChangeOperatorId,
        onSearchOperator,
        onSearchStyle,
        onSearchSize,
        onSearchSupervisor,
        onSearchPO,
        dailyFinishingLinePlanData,
        setDailyFinishingLinePlanData,
      }}
    >
      {children}
    </DailyFinishingLinePlanContext.Provider>
  );
};

export default DailyFinishingLinePlanContextProvider;
